import React from 'react';
import { Typography } from 'interfaces/typography';
import { Condition, Play } from 'interfaces/plays';
import { UserGroup } from 'interfaces/db';
import { CnfFilterType } from 'interfaces/plays';
import { HelpCircle } from 'lucide-react';

type PlayGroup = {
  type: 'everyone' | 'user' | 'group';
  userId?: string;
  userName?: string;
  groupId?: string;
  groupName?: string;
  conditions: Condition[];
};

type PlaysSidebarProps = {
  conditions: Condition[];
  plays: Play[];
  groupIdToGroup: Map<string, UserGroup>;
  visibleAccounts: any;
  onConditionClick: (conditionId: string) => void;
  expandedConditions: Set<string>;
};

type PlaysSidebarState = {
  collapsed: boolean;
};

export class PlaysSidebar extends React.Component<PlaysSidebarProps, PlaysSidebarState> {
  constructor(props: PlaysSidebarProps) {
    super(props);
    this.state = {
      collapsed: false,
    };
  }

  _organizePlaysByUserGroups(): PlayGroup[] {
    const groups: PlayGroup[] = [];
    const everyoneConditions: Condition[] = [];
    const userConditionsMap = new Map<string, Condition[]>();
    const groupConditionsMap = new Map<string, Condition[]>();

    for (const condition of this.props.conditions) {
      if (!condition.active) continue;

      // Find USER filter in the condition
      const userFilter = condition.filter_cnf.find(f => f.filter_type === CnfFilterType.USER);

      if (!userFilter || !userFilter.user_filters || userFilter.user_filters.length === 0) {
        // No user filter = everyone
        everyoneConditions.push(condition);
      } else {
        // Check for user groups and individual users
        for (const filter of userFilter.user_filters) {
          if (filter.user_group_id) {
            // Group filter
            if (!groupConditionsMap.has(filter.user_group_id)) {
              groupConditionsMap.set(filter.user_group_id, []);
            }
            groupConditionsMap.get(filter.user_group_id)!.push(condition);
          } else if (filter.user_id) {
            // Individual user filter
            if (!userConditionsMap.has(filter.user_id)) {
              userConditionsMap.set(filter.user_id, []);
            }
            userConditionsMap.get(filter.user_id)!.push(condition);
          }
        }
      }
    }

    // Add "Everyone" group
    if (everyoneConditions.length > 0) {
      groups.push({
        type: 'everyone',
        conditions: everyoneConditions,
      });
    }

    // Add user groups
    for (const [groupId, conditions] of groupConditionsMap.entries()) {
      const group = this.props.groupIdToGroup.get(groupId);
      groups.push({
        type: 'group',
        groupId,
        groupName: group?.user_group_name ?? 'Unknown Group',
        conditions,
      });
    }

    // Add individual users
    for (const [userId, conditions] of userConditionsMap.entries()) {
      const user = this.props.visibleAccounts?.users?.find((u: any) => u.user_id === userId);
      groups.push({
        type: 'user',
        userId,
        userName: user?.user_name ?? 'Unknown User',
        conditions,
      });
    }

    return groups;
  }

  _getPlayCountForCondition(condition: Condition): number {
    return this.props.plays.filter(
      p =>
        p.active &&
        ((p.condition_id === condition.condition_id && condition.condition_id !== '__DEFAULT__') ||
          (p.temp_condition_id && p.temp_condition_id === condition.temp_condition_id))
    ).length;
  }

  _renderGroupIcon(type: 'everyone' | 'user' | 'group'): JSX.Element {
    if (type === 'everyone') {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-blue-500">
          <path
            d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75M13 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    } else if (type === 'group') {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-green-500">
          <path
            d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    } else {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-purple-500">
          <path
            d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
  }

  _renderPlayGroup(group: PlayGroup, index: number): JSX.Element {
    const displayName =
      group.type === 'everyone'
        ? 'Everyone'
        : group.type === 'group'
        ? group.groupName
        : group.userName;

    return (
      <div key={index} className="mb-4">
        <div className="flex items-center gap-2 mb-2 px-3">
          {this._renderGroupIcon(group.type)}
          <Typography variant="mediumParagraph" color="#1F2937" fontWeight={600}>
            {displayName}
          </Typography>
          <span className="ml-auto text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            {group.conditions.length}
          </span>
        </div>
        <div className="space-y-1">
          {group.conditions.map((condition) => {
            const playCount = this._getPlayCountForCondition(condition);
            const conditionKey = condition.temp_condition_id || condition.condition_id;
            const isExpanded = this.props.expandedConditions.has(conditionKey);

            return (
              <button
                key={conditionKey}
                onClick={() => this.props.onConditionClick(conditionKey)}
                className={`w-full text-left px-3 py-2 rounded-md transition-colors duration-150 ${
                  isExpanded
                    ? 'bg-blue-100 border-l-2 border-blue-500'
                    : 'hover:bg-gray-100 border-l-2 border-transparent'
                }`}
              >
                <div className="flex items-center justify-between">
                  <Typography
                    variant="smallParagraph"
                    color={isExpanded ? '#2563EB' : '#374151'}
                    fontWeight={isExpanded ? 600 : 400}
                    className="truncate"
                  >
                    {condition.condition_name || 'Unnamed Condition'}
                  </Typography>
                  {playCount > 0 && (
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded ${
                        isExpanded
                          ? 'bg-blue-200 text-blue-700'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {playCount}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  render() {
    const playGroups = this._organizePlaysByUserGroups();

    if (this.state.collapsed) {
      return (
        <div className="h-full bg-white border-r border-gray-200 flex flex-col items-center py-4">
          <button
            onClick={() => this.setState({ collapsed: false })}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            title="Expand sidebar"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              className="text-gray-600"
            >
              <path
                d="M9 18l6-6-6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      );
    }

    return (
      <div
        className="h-full bg-white border-r border-gray-200 flex flex-col"
        style={{ width: '280px', minWidth: '280px' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <Typography variant="h3" color="#1F2937" fontWeight={600}>
            Plays
          </Typography>
          <div className="flex items-center gap-2">
            <a
              href="https://www.tella.tv/video/trellis-plays-scripts-walkthrough-6rqt"
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 hover:bg-blue-50 rounded-md transition-colors group"
              title="Watch Plays & Scripts tutorial"
            >
              <HelpCircle className="w-4 h-4 text-gray-600 group-hover:text-blue-600" />
            </a>
            <button
              onClick={() => this.setState({ collapsed: true })}
              className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
              title="Collapse sidebar"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                className="text-gray-600"
              >
                <path
                  d="M15 18l-6-6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto py-4">
          {playGroups.length === 0 ? (
            <div className="px-4 text-center">
              <Typography variant="smallParagraph" color="#9CA3AF">
                No active plays
              </Typography>
            </div>
          ) : (
            playGroups.map((group, index) => this._renderPlayGroup(group, index))
          )}
        </div>
      </div>
    );
  }
}
